import React from 'react';
import { Button } from '../ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Sparkles, User } from 'lucide-react';
import { PERSONALIZATION_PLACEHOLDERS } from '@/lib/email/placeholder-utils';

interface PersonalizationToolbarProps {
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  value: string;
  onInsert: (newValue: string) => void;
}

/**
 * Toolbar for adding personalization placeholders to email messages.
 * Displays an "Add Personal Touch" button with a dropdown of available placeholders.
 * Inserts placeholders at the current cursor position in the textarea.
 */
export const PersonalizationToolbar: React.FC<PersonalizationToolbarProps> = ({
  textareaRef,
  value,
  onInsert,
}) => {
  const [open, setOpen] = React.useState(false);

  const handleInsert = (placeholder: string) => {
    const textarea = textareaRef.current;

    if (!textarea) {
      // Fallback: append to end if no textarea ref
      onInsert(value + placeholder);
      setOpen(false);
      return;
    }

    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;

    // Insert placeholder at cursor position
    const newValue = value.substring(0, start) + placeholder + value.substring(end);
    onInsert(newValue);

    // Close popover and restore focus with cursor after inserted text
    setOpen(false);

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + placeholder.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  return (
    <div className="mb-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2 text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300 dark:text-blue-400 dark:border-blue-800 dark:hover:bg-blue-950"
            aria-label="Add personalization placeholder"
          >
            <Sparkles className="h-4 w-4" />
            Add Personal Touch
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <div className="p-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-2 py-1">
              Add Personal Touch
            </p>
            <div className="space-y-1 mt-1">
              {PERSONALIZATION_PLACEHOLDERS.map((placeholder) => (
                <button
                  key={placeholder.value}
                  type="button"
                  className="w-full flex items-start gap-3 p-2 rounded-md hover:bg-muted transition-colors text-left"
                  onClick={() => handleInsert(placeholder.value)}
                >
                  <User className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{placeholder.label}</div>
                    <div className="text-xs text-muted-foreground">
                      {placeholder.description}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="border-t mt-2 pt-2 px-2">
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">Default fallback:</span>{' '}
                Shows friendly text when customer name isn't available
              </p>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default PersonalizationToolbar;
